import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";
import {
  productToFormValues,
  toProductPayload,
} from "@/schema/product/product";

const API_BASE = `${BACKEND_URL}/products`;

/**
 * Product Service (ProductController - /products, /product-extras,
 * /product-relations). Escopado pela empresa do token.
 * O `id` do produto nas rotas é o code UUID.
 */
class ProductService {
  /**
   * Produtos da empresa, inclusive indisponíveis (painel)
   * @param {{category?: string, search?: string}} [filters]
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array>}
   */
  async getAdminAll(filters = {}, options = {}) {
    try {
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;
      const response = await api.get(`${API_BASE}/admin/all`, {
        ...options,
        params,
      });
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar produtos:", error);
      throw error;
    }
  }

  async getById(id) {
    try {
      const response = await api.get(`${API_BASE}/${id}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar produto ${id}:`, error);
      throw error;
    }
  }

  /** @returns {Promise<boolean>} produto disponível? */
  async verify(id) {
    const response = await api.get(`${API_BASE}/${id}/verify`, {
      silent: true,
    });
    return response.data === true;
  }

  async create(product) {
    try {
      const response = await api.post(API_BASE, product);
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar produto:", error);
      throw error;
    }
  }

  async update(id, product) {
    try {
      const response = await api.put(`${API_BASE}/${id}`, product);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar produto ${id}:`, error);
      throw error;
    }
  }

  /**
   * Pausa/ativa o produto no cardápio. O PUT substitui ingredientes, combo e
   * adicionais, e `quantity` regrava o estoque: por isso parte do produto
   * completo (GET /products/{id}) e envia o mesmo payload do formulário,
   * mudando só `available`.
   * @returns {Promise<object|null>} produto atualizado
   */
  async setAvailability(id, available) {
    try {
      const full = await this.getById(id);
      if (!full) throw new Error(`Produto ${id} não encontrado`);
      const payload = toProductPayload(
        productToFormValues(full),
        full.ingredients
      );
      payload.available = Boolean(available);
      return await this.update(id, payload);
    } catch (error) {
      console.error(`❌ Erro ao alterar disponibilidade do produto ${id}:`, error);
      throw error;
    }
  }

  /**
   * Remove o produto. Com pedidos, estoque ou combo vinculado a API devolve
   * 400; nesse caso a tela oferece marcar como indisponível.
   * O erro é silencioso para a tela decidir a mensagem.
   */
  async delete(id) {
    try {
      await api.delete(`${API_BASE}/${id}`, { silent: true });
    } catch (error) {
      console.error(`❌ Erro ao excluir produto ${id}:`, error);
      throw error;
    }
  }

  /**
   * Envia a imagem do produto (multipart, campo `file`, até 5 MB)
   * @returns {Promise<object|null>} produto atualizado
   */
  async uploadImage(id, file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post(`${API_BASE}/${id}/image`, formData);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao enviar imagem do produto ${id}:`, error);
      throw error;
    }
  }

  /** Produtos usados como extra/combo */
  async getExtras() {
    try {
      const response = await api.get(`${BACKEND_URL}/product-extras`);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar produtos extras:", error);
      throw error;
    }
  }

  /** Relações pai → filho de combos */
  async getRelations() {
    try {
      const response = await api.get(`${BACKEND_URL}/product-relations`);
      return unwrapList(response);
    } catch (error) {
      console.error("❌ Erro ao buscar relações de produtos:", error);
      throw error;
    }
  }

  async addRelation(parentId, childId) {
    try {
      const response = await api.post(`${BACKEND_URL}/product-relations`, {
        parentId,
        childId,
      });
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao vincular produtos:", error);
      throw error;
    }
  }

  async removeRelation(parentId, childId) {
    try {
      await api.delete(`${BACKEND_URL}/product-relations`, {
        data: { parentId, childId },
      });
    } catch (error) {
      console.error("❌ Erro ao desvincular produtos:", error);
      throw error;
    }
  }
}

export default new ProductService();
